import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../api/apiService';
import agui from '../utils/aguiClient';
import { useAuth } from './AuthContext';
import { processMessagesWithReferences } from '../utils/referenceFormatter';

// 创建聊天上下文
const ChatContext = createContext();

// 聊天提供者组件
export const ChatProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingChatId, setLoadingChatId] = useState(null); // 防止重复加载同一个聊天
  const [chatStreamingStates, setChatStreamingStates] = useState(new Map()); // 为每个会话维护独立的streaming状态
  const [currentMessageId, setCurrentMessageId] = useState(null);
  const [currentModel, setCurrentModel] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [useDeepResearchStep, setUseDeepResearchStep] = useState(0); // 深度研究步骤变量
  
  // 使用 useRef 保存 currentChat 的最新值
  const currentChatRef = useRef(currentChat);
  const currentMessageIdRef = useRef(currentMessageId);

  
  // 深度研究相关状态
  const [researchDrawerOpen, setResearchDrawerOpen] = useState(false);
  const [researchContent, setResearchContent] = useState('');
  const [researchMessageId, setResearchMessageId] = useState(null); // 添加存储消息ID的状态
  const [researchingMessages, setResearchingMessages] = useState(new Map()); // 存储正在研究的消息
  const [completedResearch, setCompletedResearch] = useState(new Map()); // 存储完成的研究结果
  const [researchedMessages, setResearchedMessages] = useState(new Map()); // 存储已经研究完的参数
  const pollingIntervalsRef = useRef(new Map()); // 使用useRef存储轮询定时器，确保同步访问

  // 思考相关状态
  const [thinkingDrawerOpen, setThinkingDrawerOpen] = useState(false);
  const [thinkingContent, setThinkingContent] = useState('');
  const [thinkingMessageId, setThinkingMessageId] = useState(null);
  const [thinkingTime, setThinkingTime] = useState(null); // 思考时间
  const [thinkingMessages, setThinkingMessages] = useState(new Map()); // 存储思考内容
  const [thinkingTimeMap, setThinkingTimeMap] = useState(new Map()); // 存储思考时间

  const chatInputRef = useRef(null);

  // 更新 currentChatRef 当 currentChat 状态变化时
  useEffect(() => {
    console.log("currentChat========:", currentChat);
    currentChatRef.current = currentChat;
  }, [currentChat]);

  useEffect(() => { 
    console.log("currentMessageId========:", currentMessageId);
    currentMessageIdRef.current = currentMessageId;
  }, [currentMessageId]);

  // 加载模型列表
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await apiService.getModelList();
        if (response.code === 200 && response.data) {
          setAvailableModels(response.data);
          
          // 设置默认选中的模型
          const defaultModel = response.data.find(model => model.selected) || response.data[0];
          if (defaultModel) {
            setCurrentModel(defaultModel);
          }
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
      }
    };
    
    const fetchAppInfo = async () => {
      try {
        const response = await apiService.getAppInfo();
        if (response.code === 200 && response.data) {
          setWelcomeMessage(response.data.welcome);
        }
      } catch (error) {
        console.error('Failed to fetch app info:', error);
      }
    };
    
    fetchModels();
    fetchAppInfo();
  }, []);

  // 当welcomeMessage更新时，如果当前有欢迎消息，则更新它
  useEffect(() => {
    if (welcomeMessage && messages.length > 0) {
      const welcomeMsg = messages.find(msg => msg.messageId === 'welcome');
      if (welcomeMsg && welcomeMsg.answer !== welcomeMessage) {
        setMessages(prev => prev.map(msg => 
          msg.messageId === 'welcome' 
            ? { ...msg, answer: welcomeMessage }
            : msg
        ));
      }
    }
  }, [welcomeMessage, messages]);

  // 初始加载聊天历史
  useEffect(() => {
    if (isAuthenticated) {
      loadChatHistory();
    }
  }, [isAuthenticated]);  // 当认证状态改变时执行
  
  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      // 组件卸载时停止所有轮询
      pollingIntervalsRef.current.forEach((intervalId) => {
        clearInterval(intervalId);
      });
      pollingIntervalsRef.current.clear();
    };
  }, []);

  // 加载聊天历史
  const loadChatHistory = async (pageSize = 20, pageNum = 1, append = false) => {
    try {

      const response = await apiService.getHistoryList(pageSize, pageNum);
      if (response.code === 200) {
        if (append) {
          // 追加模式，将新数据追加到现有数据后面
          setChatHistory(prev => [...prev, ...(response.data || [])]);
          // 返回数据以便调用者判断是否还有更多数据
          return response.data || [];
        } else {
          // 替换模式，直接设置新数据
          setChatHistory(response.data || []);
          if (!currentChat && response.data && response.data.length > 0) {
            setCurrentChat(response.data[0]);
          }
        }
      }
      return [];
    } catch (error) {
      console.error('Failed to load chat history:', error);
      return [];
    } finally {

    }
  };

  // 加载特定聊天的消息
  const loadChatMessages = async (chatlistId, pageSize = 1000, pageNum = 1) => {
    // 防止重复加载同一个聊天
    if (loadingChatId === chatlistId || loading) {
      console.log('跳过加载，已在加载中或正在加载该聊天');
      return;
    }
    
    // 如果当前已经是该聊天且有消息，也跳过
    if (currentChat?.chatlistId === chatlistId && messages.length > 0) {
      console.log('跳过加载，当前已是该聊天且有消息');
      return;
    }
    
    try {
      setLoading(true);
      setLoadingChatId(chatlistId); // 设置正在加载的聊天ID
      setUseDeepResearchStep(0); // 重置深度研究步骤变量
      
      // 停止之前的所有轮询
      stopAllPolling();
      
      // 断开当前的SSE连接
      agui.stop();
      
      const response = await apiService.getHistoryDetail(chatlistId, pageSize, pageNum);
      if (response.code === 200) {
        console.log('获取历史记录详情成功:', response.data);
        // 处理消息中的answerWeb字段，将参考资料追加到answer后面
        const processedMessages = processMessagesWithReferences(response.data || []);

        // // 添加测试数据 - 思考功能测试
        // const testThinkingMessage = {
        //   messageId: `test-thinking-${Date.now()}`,
        //   query: '这是一条测试思考功能的消息',
        //   answer: '这是AI的回答内容。点击下方的思考详情按钮可以查看AI的思考过程。',
        //   answerThinking: '**AI的思考过程：**\n\n1. 首先分析用户的问题...\n2. 然后考虑可能的解决方案...\n3. 最后得出最佳答案...\n\n思考是一个复杂的过程，需要综合考虑多个因素。',
        //   answerThinkingTime: "80",
        //   isUser: false,
        //   createTime: new Date().toISOString(),
        //   feedback: 0
        // };
        // processedMessages.push(testThinkingMessage);

        // 如果有欢迎消息，将其添加到消息列表的第一条位置
        const welcomeMsg = {
          messageId: 'welcome',
          answer: welcomeMessage || '欢迎使用我们的AI助手！',
          isUser: false,
          createTime: new Date().toISOString()
        };
        
        // 将欢迎消息插入到消息列表的开头
        setMessages([welcomeMsg, ...processedMessages]);
           setChatStreamingState(chatlistId, false);
        
        // 检查最后一条消息的状态
        const lastMessage = processedMessages[processedMessages.length - 1];
        if (lastMessage && lastMessage.status === 2) { // 状态为2表示继续
          console.log(`发现状态为2的消息，需要重连SSE: ${lastMessage.messageId}`);
          // 设置该会话为streaming状态
          setChatStreamingState(chatlistId, true);
          setCurrentMessageId(lastMessage.messageId);
          // 调用SSE重连接口
          try {
            // 断开当前的SSE连接
            agui.stop();
            // 清除所有事件监听器
            agui.clearAllListeners();
            
            // 设置超时处理，最长等待30秒
            const timeoutId = setTimeout(() => {
              console.log('SSE重连超时，自动结束');
              setChatStreamingState(chatlistId, false);
              setCurrentMessageId(null);
            }, 9000000);
            
            // 设置事件监听器
            setupEventListeners(timeoutId);
            
            // 调用重连方法
            await agui.reconnect(lastMessage.messageId);
          } catch (reconnectError) {
            console.error('SSE重连失败:', reconnectError);
            // 重连失败时，清除流式状态
            setChatStreamingState(chatlistId, false);
            setCurrentMessageId(null);
          }
        } else {
          // 已经结束，需要设置思考时间为0
          setThinkingTimeMap(prev => {
            const newMap = new Map(prev);
            newMap.set(lastMessage.messageId, 0);
            return newMap;
          });
          // 检查是否有需要轮询的消息（answerDeep 为 'researching' 且是最后一条）
          if (lastMessage && lastMessage.answerDeep === 'researching' && lastMessage.messageId) {
            console.log(`发现需要轮询的消息: ${lastMessage.messageId}`);
            startPollingForMessage(lastMessage.messageId, chatlistId);
          }
        }
        
        const selectedChat = chatHistory.find(chat => chat.chatlistId === chatlistId);
        if(selectedChat){
          setCurrentChat(selectedChat );
        }
      }
    } catch (error) {
      console.error('Failed to load chat messages:', error);
    } finally {
      setLoading(false);
      setLoadingChatId(null); // 清除加载状态
    }
  };

  // 发送消息
  const sendMessage = async (content, file = [], useSearch = false, useDeepResearch = false) => {
    if (!content && file.length === 0) return;
    
    // 处理深度研究步骤变量
    let currentStep = useDeepResearchStep;
    if (!useDeepResearch) {
      // 深度研究按钮没有勾选，设置步骤变量为0
      currentStep = 0;
      setUseDeepResearchStep(0);
    } else {
      // 深度研究按钮已勾选，判断当前步骤变量状态
      if (currentStep === 0) {
        currentStep = 1;
        setUseDeepResearchStep(1);
      } else if (currentStep === 1) {
        currentStep = 2;
        setUseDeepResearchStep(2);
      } else if (currentStep === 2) {
        currentStep = 1;
        setUseDeepResearchStep(1);
      }
    }
    
    console.log('深度研究步骤变量:', currentStep, '深度研究开关:', useDeepResearch);
    
    // 格式化文件数据 - 确保文件数据包含id和name结构
    const formattedFiles = file.map(file => ({
      id: file.id,
      name: file.name || file.originalFilename
    }));
    
    // 生成临时消息ID
    const tempMessageId = `temp_${Date.now()}`;
    const userMessage = {
      messageId: tempMessageId,
      query: content,
      file: formattedFiles,
      isUser: true,
      createTime: new Date().toISOString()
    };
    
    // 添加用户消息到列表
    setMessages(prev => [...prev, userMessage]);
    
    // 创建新的系统消息占位符
    const systemMessage = {
      messageId: null,
      answer: '',
      isUser: false,
      streaming: true,
      createTime: new Date().toISOString()
    };
    
    // 添加系统消息占位符
    setMessages(prev => [...prev, systemMessage]);
    
    // 设置为流式状态
    if (currentChat && currentChat.chatlistId) {
      setChatStreamingState(currentChat.chatlistId, true);
    }
    
    // 设置超时处理，最长等待30秒
    const timeoutId = setTimeout(() => {
      // 检查会话级streaming状态
      let isChatStreaming = false;
      if (currentChat && currentChat.chatlistId) {
        isChatStreaming = getChatStreamingState(currentChat.chatlistId);
      }
      
      if (isChatStreaming) {
        console.log('流式响应超时，自动结束');
        if (currentChat && currentChat.chatlistId) {
          setChatStreamingState(currentChat.chatlistId, false);
        }
        setCurrentMessageId(null);
        
        // 更新消息列表，清除流式状态
        setMessages(prev => prev.map(msg => 
          msg.streaming ? { ...msg, streaming: false } : msg
        ));
      }
    }, 30000);
    
    try {

      // 清除所有事件监听器
      agui.clearAllListeners();
      // 设置事件监听器
      setupEventListeners(timeoutId);
      
      // 获取当前模型ID，确保使用最新的模型
      const modelId = currentModel?.modelId;
      console.log('发送消息使用的模型ID:', modelId, '当前模型:', currentModel?.name);
      
      // 使用AG-UI协议发送消息
      await agui.sendInput({
        type: 'message',
        chatlistId: currentChat?.chatlistId === "newchat" ? undefined : currentChat?.chatlistId,
        content,
        file: formattedFiles,
        modelId: modelId,
        use_search: useSearch,
        use_deep_research: useDeepResearch,
        use_deep_research_step: currentStep
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      if (currentChat && currentChat.chatlistId) {
        setChatStreamingState(currentChat.chatlistId, false);
      }
      clearTimeout(timeoutId); // 清除超时计时器
    }
  };

  // 设置事件监听器
  const setupEventListeners = (timeoutId) => {
    console.log('设置事件监听器');
    
    agui.on('messageStarted', (data) => {


      console.log('收到messageStarted事件:', data);
      setCurrentMessageId(data.messageId);
      // 如果当前没有选中的聊天，使用返回的chatlistId更新currentChat
      if ((!currentChatRef.current || currentChatRef.current.chatlistId === "newchat") && data.chatlistId) {

        setCurrentChat({ ...currentChatRef.current, chatlistId: data.chatlistId ,messageId: data.messageId});
        setChatStreamingState(data.chatlistId, true);
        // 同时将新的chatlistId保存到localStorage中
        localStorage.setItem('selectedChatId', data.chatlistId);

        setTimeout(() => {
          loadChatHistory(20, 1, false).then(() => {
            loadChatMessages(data.chatlistId);
          });
        }, 3500);
      }else{
        
        // 同时更新chatHistory中的对应项
        setChatHistory(prevHistory => {
          const newUpdateTime = new Date().toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(/\//g, '-');
          
          // 找到当前聊天项
          const currentChatIndex = prevHistory.findIndex(chat => chat.chatlistId === currentChatRef.current.chatlistId);
          if (currentChatIndex === -1) return prevHistory; // 如果没找到，返回原数组
          
          // 创建更新后的聊天项
          const updatedChat = {
            ...prevHistory[currentChatIndex],
            updateTime: newUpdateTime
          };
          
          // 从原数组中移除当前聊天项，并将更新后的聊天项放在数组开头
          const newHistory = [
            updatedChat,
            ...prevHistory.slice(0, currentChatIndex),
            ...prevHistory.slice(currentChatIndex + 1)
          ];
          
          return newHistory;
        });
      }
      // 更新消息列表中的messageId
      setMessages(prev => {
        console.log('messageStarted前的消息列表:', prev);
        const updated = prev.map(msg => 
          (msg.messageId === data.messageId || (msg.streaming && !msg.messageId))  ?
           { ...msg, messageId: data.messageId } : msg
        );
        console.log('messageStarted后的消息列表:', updated);
        return updated;
      });
      // 重置思考时间和思考消息
      setThinkingTimeMap(new Map());
      setThinkingMessages(new Map());

    });
    
    agui.on('messageChunk', (data) => {
      console.log('messageChunk事件触发:', data);
      // messageChunk事件中不再设置currentChat，因为应该在messageStarted中已经设置了
      // 只有在极少数情况下（如messageStarted未触发但messageChunk先到达）才需要设置
      // if (!currentChatRef.current && data.chatlistId) {
      //   console.log('当前无聊天会话，设置新的chatlistId:', data.chatlistId);
      //   setCurrentChat({ chatlistId: data.chatlistId });
      //   // 同时更新聊天历史并加载消息
      //   loadChatHistory(20, 1, false).then(() => {
      //     loadChatMessages(data.chatlistId);
      //   });
      // }
      // 追加消息内容 - 修改为累加内容而不是替换   
      setMessages(prev => {
        // 找到需要更新的消息
        const streamingMsg = prev.find(msg =>
          msg.streaming || msg.messageId === data.messageId
        );

        const updated = prev.map(msg =>
          (msg.messageId === data.messageId || (msg.streaming && !msg.messageId)) ?
            {
              ...msg,
              messageId: data.messageId,
              answer: (msg.answer || '') + data.content  // 累加内容
            } : msg
        );
        return updated;
      });
    });

    
    agui.on('messageDone', (data) => {
      console.log('messageDone data:', data);
      // 清除超时计时器
      if (timeoutId) {
        console.log('清除超时计时器');
        clearTimeout(timeoutId);
      }
      
      // 结束流式状态
      console.log('设置streaming状态为false');
      // 如果有当前聊天，设置该聊天的streaming状态为false

      if (currentChatRef.current && currentChatRef.current.chatlistId) {
        setChatStreamingState(currentChatRef.current.chatlistId, false);
      }
      setCurrentMessageId(null);
      
      // 更新消息列表，清除流式状态
      setMessages(prev => {
        console.log('messageDone前的消息列表:', prev);
        // 查找streaming状态的消息
        const streamingMsg = prev.find(msg => msg.streaming);
        console.log('需要更新streaming状态的消息:', streamingMsg);
        
        const updated = prev.map(msg => 
          msg.streaming ? { ...msg, streaming: false } : msg
        );
        console.log('messageDone后的消息列表:', updated);
        return updated;
      });
    });
    
    agui.on('loginRequired', (data) => {
      // 未授权，跳转到登录页
      localStorage.removeItem('token');
      window.location.href = '/login';
    });
    
    // 深度研究事件监听
    agui.on('researchingChunk', (data) => {
      
      // 存储正在研究的消息内容
      setResearchingMessages(prev => {
        const newMap = new Map(prev);
        newMap.set(data.messageId, data.content);
        return newMap;
      });
      //每次都累加内容
      setResearchedMessages(prev => {
        const newMap = new Map(prev);
        const existingContent = newMap.get(data.messageId) || '';
        newMap.set(data.messageId, existingContent + data.content);
        return newMap;
      });
    });
    
    agui.on('researchResult', (data) => {

      setCompletedResearch(prev => {
        const newMap = new Map(prev);
        newMap.set(data.messageId, data.content);
        return newMap;
      });
    });

    // 知识库引用事件监听
    agui.on('kbSource', (data) => {
      // console.log('收到知识库引用事件:', data);
      // 将知识库引用数据添加到对应的消息中
      setMessages(prev => {
        return prev.map(msg =>
          msg.messageId === data.messageId
            ? { ...msg, kbSource: data.kbSource }
            : msg
        );
      });
    });

    // 思考内容事件监听
    agui.on('answerThinking', (data) => {
      console.log('收到思考内容事件:', data);
      // 存储思考内容到Map中
      setThinkingMessages(prev => {
        const newMap = new Map(prev);
        const existingContent = newMap.get(data.messageId) || '';
        newMap.set(data.messageId, existingContent + data.content);
        return newMap;
      });
    });

    // 思考时间事件监听
    agui.on('answerThinkingTime', (data) => {
      console.log('收到思考时间事件:', data);
      // 存储思考时间到Map中
      setThinkingTimeMap(prev => {
        const newMap = new Map(prev);
        newMap.set(data.messageId, data.time);
        return newMap;
      });
    });

    // 连接超时事件监听
    agui.on('connectionTimeout', (data) => {
     
      // 连接超时时的处理逻辑
      if (data.reason === 'heartbeat_timeout') {
        
        // 在界面上显示超时信息
        setMessages(prev => {
          const streamingMsg = prev.find(msg => msg.streaming);
          if (streamingMsg) {
            return prev.map(msg => 
              msg.streaming ? { 
                ...msg, 
                streaming: false,
                answer: (msg.answer || '') + '\n\n⚠️ 网络连接超时，请刷新页面后重试' 
              } : msg
            );
          }
          return prev;
        });
        
        // 结束流式状态
        if (currentChatRef.current && currentChatRef.current.chatlistId) {
          setChatStreamingState(currentChatRef.current.chatlistId, false);
        }
        setCurrentMessageId(null);
        
        // 清除超时计时器
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    });
  };

  // 停止流式响应
  const stopStreaming = async () => {
    console.log("stopStreaming");
    // 如果有当前聊天，检查该聊天的streaming状态
    let isChatStreaming = false;
    if (currentChatRef.current && currentChatRef.current.chatlistId) {
      isChatStreaming = getChatStreamingState(currentChatRef.current.chatlistId);
    }

    console.log('stopStreaming参数:', {
      isChatStreaming,
      currentMessageId:currentMessageIdRef.current ,
      currentChat: currentChatRef.current
    });
    
    if (!isChatStreaming || !currentMessageId || !currentChatRef.current) return;
    try {
      // 停止SSE流
      agui.stop();
      
      // 调用后端停止接口（异步调用，不等待结果）
      apiService.stopAnswer(currentChatRef.current.chatlistId, currentMessageId).catch(error => {
        console.error('Failed to stop answer on backend:', error);
      });
      
      // 如果有当前聊天，设置该聊天的streaming状态为false
      if (currentChatRef.current && currentChatRef.current.chatlistId) {
        setChatStreamingState(currentChatRef.current.chatlistId, false);
      }
      
      // 更新消息列表，清除流式状态，并在answer后追加"已被停止"
      setMessages(prev => prev.map(msg => 
        msg.streaming ? { ...msg, streaming: false, status: 4, answer: (msg.answer || '') + '已被停止' } : msg
      ));
    } catch (error) {
      console.error('Failed to stop streaming:', error);
    }
  };
  
  // 发送反馈（点赞/点踩）
  const sendFeedback = async (messageId, feedback) => {
    if (!currentChatRef.current || !messageId) return;
    
    try {
      await apiService.sendFeedback(currentChatRef.current.chatlistId, messageId, feedback);
      
      // 更新消息列表中的反馈状态
      setMessages(prev => prev.map(msg => 
        msg.messageId === messageId ? { ...msg, feedback } : msg
      ));
    } catch (error) {
      console.error('Failed to send feedback:', error);
    }
  };
  
  // 获取特定会话的streaming状态
  const getChatStreamingState = (chatlistId) => {
    if (!chatlistId) return false;
    return chatStreamingStates.get(chatlistId) || false;
  };
  
  // 设置特定会话的streaming状态
  const setChatStreamingState = (chatlistId, isStreaming) => {
    console.log('setChatStreamingState=======', chatlistId, isStreaming);
    if (!chatlistId) return;
    setChatStreamingStates(prev => {
      const newMap = new Map(prev);
      newMap.clear(); // 每次进来都删除以前所有的状态
      if (isStreaming) {
        newMap.set(chatlistId, true);
      }
      return newMap;
    });
  };

  // 更新特定聊天记录的状态
  const updateChatHistoryItem = useCallback((chatlistId, updates) => {
    setChatHistory(prev => prev.map(chat => 
      chat.chatlistId === chatlistId ? { ...chat, ...updates } : chat
    ));
  }, []);

  // 更新当前聊天的状态
  const updateCurrentChat = useCallback((updates) => {

    setCurrentChat(prev => ({ ...prev, ...updates }));
    // 同时更新chatHistory中的对应项
    if (currentChat?.chatlistId) {
      if (currentChat.chatlistId === "newchat") {
      } else {
        // 正常更新
        updateChatHistoryItem(currentChat.chatlistId, updates);
      }
    }
  }, [currentChat, updateChatHistoryItem]);

  // 切换模型 - 使用 useCallback 确保函数稳定性
  const switchModel = useCallback((modelId) => {
    const model = availableModels.find(m => m.modelId === modelId);
    if (model) {
      console.log('切换模型:', model);
      setCurrentModel(model);
      
      // 更新当前聊天记录中的模型ID
      if (currentChat?.chatlistId) {
        updateCurrentChat({ modelId: modelId });
      }
      
      // 强制触发重新渲染，确保所有使用currentModel的组件都能获取到最新值
      setTimeout(() => {
        console.log('模型切换完成，当前模型:', model);
      }, 0);
    }
  }, [availableModels, currentChat, updateCurrentChat]);
  
  // 获取当前模型的函数
  const getCurrentModel = useCallback(() => {
    return currentModel;
  }, [currentModel]);
  
  // 聚焦输入框
  const focusChatInput = () => {
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  };

  // 编辑消息并重新发送
  const editMessage = async (newContent) => {
    if (!newContent.trim()) return;
    
    // 发送新的消息
    await sendMessage(newContent.trim());
  };
  
  // 深度研究相关方法
  const openResearchDrawer = (content, messageId = null) => {
    setResearchContent(content);
    setResearchMessageId(messageId); // 保存消息ID
    setResearchDrawerOpen(true);
  };
  
  const closeResearchDrawer = () => {
    setResearchDrawerOpen(false);
    setResearchContent('');
    setResearchMessageId(null); // 清除消息ID
  };
  
  // 获取消息的深度研究状态
  const getResearchStatus = (messageId) => {
    if (completedResearch.has(messageId)) {
      return {
        isResearching: false,
        content: completedResearch.get(messageId),
        detailContent: researchedMessages.get(messageId)
      };
    }
    if (researchingMessages.has(messageId)) {
      return {
        isResearching: true,
        content: researchingMessages.get(messageId),
        detailContent: researchedMessages.get(messageId)
      };
    }
    

    
    return null;
  };
  
  // 开始对特定消息的轮询
  const startPollingForMessage = (messageId, chatlistId) => {
    console.log(`开始轮询消息 ${messageId}`);
    
    // 如果已经在轮询，先停止
    if (pollingIntervalsRef.current.has(messageId)) {
      clearInterval(pollingIntervalsRef.current.get(messageId));
    }
    
    const pollFunction = async () => {
      try {
        console.log(`轮询检查消息 ${messageId}`);
        const response = await apiService.getHistoryDetail(chatlistId, 20, 1);
        
        if (response.code === 200 && response.data) {
          // 查找对应的消息
          const targetMessage = response.data.find(msg => msg.messageId === messageId);
          
          if (targetMessage && targetMessage.answerDeep) {
            if (targetMessage.answerDeep !== 'researching') {
              // 研究完成，停止轮询并更新状态
              console.log(`消息 ${messageId} 研究完成`);
              stopPollingForMessage(messageId);
              
              // 更新消息列表，包含targetMessage中的其他内容
              setMessages(prev => prev.map(msg => 
                msg.messageId === messageId 
                  ? { 
                      ...msg, 
                      ...targetMessage, // 使用targetMessage中的所有内容
                      isUser: msg.isUser, // 保持原有的isUser属性
                      streaming: msg.streaming // 保持原有的streaming属性
                    }
                  : msg
              ));
            } else {
              console.log(`消息 ${messageId} 仍在研究中`);
            }
          }
        }
      } catch (error) {
        console.error(`轮询消息 ${messageId} 失败:`, error);
        // 发生错误时，等待下次轮询
      }
    };
    
    // 立即执行一次，然后每30秒执行一次
    pollFunction();
    const intervalId = setInterval(pollFunction, 30000); // 30秒
    
    // 同步存储轮询定时器
    pollingIntervalsRef.current.set(messageId, intervalId);
    console.log(`轮询定时器已存储: ${messageId}`, pollingIntervalsRef.current);
  };
  
  // 停止对特定消息的轮询
  const stopPollingForMessage = (messageId) => {

    
    if (pollingIntervalsRef.current.has(messageId)) {
      clearInterval(pollingIntervalsRef.current.get(messageId));
      pollingIntervalsRef.current.delete(messageId);

    } else {

    }
  };
  
  // 停止所有轮询
  const stopAllPolling = () => {


    pollingIntervalsRef.current.forEach((intervalId, messageId) => {

      clearInterval(intervalId);
    });

    pollingIntervalsRef.current.clear();

  };

  // 思考相关方法
  const openThinkingDrawer = (content, messageId = null, answerThinkingTime = null) => {
    setThinkingContent(content);
    setThinkingMessageId(messageId);
    // 如果没有传入answerThinkingTime，从Map中获取
    const thinkingTimeValue = answerThinkingTime || getThinkingTime(messageId);
    setThinkingTime(thinkingTimeValue);
    setThinkingDrawerOpen(true);
  };

  const closeThinkingDrawer = () => {
    setThinkingDrawerOpen(false);
    setThinkingContent('');
    setThinkingMessageId(null);
    setThinkingTime(null);
  };

  // 获取消息的思考内容
  const getThinkingContent = (messageId) => {
    return thinkingMessages.get(messageId) || '';
  };

  // 获取消息的思考时间
  const getThinkingTime = (messageId) => {
    console.log('getThinkingTime', thinkingTimeMap);
    return thinkingTimeMap.get(messageId) || null;
  };

  // 创建新的聊天
  const createNewChat = () => {
    setCurrentChat({chatlistId: "newchat"});
    setLoadingChatId(null); // 清除加载状态
    setUseDeepResearchStep(0); // 重置深度研究步骤变量
    
    // 重置深度研究相关状态
    setResearchingMessages(new Map());
    setCompletedResearch(new Map());
    setResearchDrawerOpen(false);
    setResearchContent('');

    // 重置思考相关状态
    setThinkingDrawerOpen(false);
    setThinkingContent('');
    setThinkingMessageId(null);
    setThinkingTime(null);
    setThinkingMessages(new Map());
    setThinkingTimeMap(new Map());

    // 停止所有轮询
    stopAllPolling();
    
    // 清除localStorage中保存的chatId，确保新聊天不会被之前的选择干扰
    localStorage.removeItem('selectedChatId');
    
    // 如果welcomeMessage已经加载完成，直接使用；否则先使用空字符串，等加载完成后通过useEffect更新
    const initialWelcomeMessage = welcomeMessage || '';
    setMessages([
      {
        messageId: 'welcome',
        answer: initialWelcomeMessage,
        isUser: true,
        createTime: new Date().toISOString(),

      }
    ]);

    // 延迟聚焦输入框，确保DOM已更新
    setTimeout(() => {
      focusChatInput();
    }, 100);
  };

  return (
    <ChatContext.Provider value={{
      chatHistory,
      currentChat,
      messages,
      loading,
      chatStreamingStates,
      getChatStreamingState,
      setChatStreamingState,
      currentModel,
      availableModels,
      welcomeMessage,
      useDeepResearchStep,
      // 深度研究相关状态和方法
      researchDrawerOpen,
      researchContent,
      researchMessageId, // 导出消息ID状态
      openResearchDrawer,
      closeResearchDrawer,
      getResearchStatus,
      // 思考相关状态和方法
      thinkingDrawerOpen,
      thinkingContent,
      thinkingMessageId,
      thinkingTime,
      openThinkingDrawer,
      closeThinkingDrawer,
      getThinkingContent,
      getThinkingTime,
      // 轮询相关方法
      startPollingForMessage,
      stopPollingForMessage,
      stopAllPolling,
      loadChatHistory,
      loadChatMessages,
      sendMessage,
      stopStreaming,
      sendFeedback,
      switchModel,
      getCurrentModel,
      createNewChat,
      focusChatInput,
      editMessage,
      chatInputRef,
      // 导出状态设置方法
      setCurrentChat,
      setMessages,
      // 添加更新聊天记录的方法
      updateCurrentChat,
      updateChatHistoryItem
    }}>
      {children}
    </ChatContext.Provider>
  );
};

// 自定义Hook，方便在组件中使用聊天上下文
export const useChat = () => useContext(ChatContext);

export default ChatContext;